import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FullPageLoadingSpinner } from 'shared/components/FullPageLoadingSpinner';
import { useDashboardId } from 'shared/hooks/useDashboardId';
import { useViewCount } from 'shared/hooks/useViewCount.ts';

export enum AuthenticationErrorEnum {
  incorrectOTP = 'incorrectOTP',
  authFailed = 'authFailed',
  logoutFailed = 'logoutFailed',
  notFound = 'notFound',
}

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthenticationLoading: boolean;
  isLogoutAllowed: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setIsAuthenticationLoading: (value: boolean) => void;
  checkAuthenticationStatus: () => Promise<void>;
  authenticationError: AuthenticationErrorEnum | null;
  setAuthenticationError: (error: AuthenticationErrorEnum | null) => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isAuthenticationLoading: true,
  isLogoutAllowed: true,
  setIsAuthenticated: () => {},
  setIsAuthenticationLoading: () => {},
  checkAuthenticationStatus: async () => {},
  authenticationError: null,
  setAuthenticationError: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}
const apiUrl = import.meta.env.VITE_API_URL;
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { dashboardId } = useDashboardId();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticationLoading, setIsAuthenticationLoading] = useState(true);
  const [isLogoutAllowed, setIsLogoutAllowed] = useState(true);
  const [authenticationError, setAuthenticationError] = useState<AuthenticationErrorEnum | null>(
    null,
  );
  const { postView } = useViewCount();

  const checkAuthenticationStatus = useCallback(async () => {
    if (!dashboardId) {
      setAuthenticationError(AuthenticationErrorEnum.notFound);
      setIsAuthenticated(false);
      setIsAuthenticationLoading(false);
      return;
    }

    setIsAuthenticationLoading(true);
    try {
      const previewToken = new URLSearchParams(window.location.search).get('previewToken');

      const url = `${apiUrl}${dashboardId}/auth-status${previewToken ? `?previewToken=${encodeURIComponent(previewToken)}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setAuthenticationError(AuthenticationErrorEnum.notFound);
        } else {
          setAuthenticationError(AuthenticationErrorEnum.authFailed);
        }
        setIsAuthenticated(false);
        return;
      }

      const data = await response.json();
      setIsAuthenticated(data.isAuthorized || false);
      setIsLogoutAllowed(data.isLogoutAllowed || false);
      if (data.isAuthorized) {
        postView();
      }
    } catch (err) {
      setAuthenticationError(AuthenticationErrorEnum.authFailed);
      setIsAuthenticated(false);
      setIsAuthenticationLoading(false);
      console.error(err);
    } finally {
      setIsAuthenticationLoading(false);
    }
  }, [dashboardId, postView]);

  useEffect(() => {
    checkAuthenticationStatus();
  }, [checkAuthenticationStatus]);

  const contextValue = useMemo(() => {
    return {
      isAuthenticated,
      isAuthenticationLoading,
      isLogoutAllowed,
      setIsAuthenticated,
      setIsAuthenticationLoading,
      checkAuthenticationStatus,
      authenticationError,
      setAuthenticationError,
    };
  }, [
    isAuthenticated,
    isAuthenticationLoading,
    isLogoutAllowed,
    authenticationError,
    checkAuthenticationStatus,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {isAuthenticationLoading ? (
        <main className="min-h-full-device sm:max-w-container mx-auto flex items-center justify-center">
          <FullPageLoadingSpinner />
        </main>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
